import { Component, OnInit } from '@angular/core';
import { BaseComponent } from '../../../base/base.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { CardComponent } from '../../../core/panels/card.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { TabPanelComponent } from '../../../core/panels/tab-panel/tab-panel.component';
import { TabComponent } from '../../../core/panels/tab-panel/tab.component';
import { AgencyPolicyComponent } from '../../policy-details/agency.component';
import { PolicyAttributesComponent } from '../../policy-details/policy-attributes.component';
import { PolicyContactsComponent } from '../../policy-details/policy-contacts.component';
import { PolicyLayerComponent } from '../../policy-details/policy-layer.component';
import { PlanSummaryResponse } from '../../../../models/api/view/claim/details/plan-summary/plan-summary-response';

@Component({
  selector: 'app-plan-summary-page',
  imports: [
    PageHeaderComponent,
    CardComponent,
    DetailPanelComponent,
    DetailItemComponent,
    TabPanelComponent,
    TabComponent,
    AgencyPolicyComponent,
    PolicyContactsComponent,
    PolicyAttributesComponent,
    PolicyLayerComponent,
  ],
  template: `<app-page-header label="Plan Summary" />
    <div class="tw-flex tw-gap-8 tw-pb-8">
      <div class="tw-basis-1/2">
        <app-card label="Plan">
          <app-detail-panel [dataSource]="planSummary.plan">
            <app-detail-item label="Plan" name="plan" />
            <app-detail-item label="Loss Fund" name="lossFund" format="c" />
            <app-detail-item label="Erosion" name="erosion" format="c" />
            <app-detail-item label="Bank" name="bank" />
            <app-detail-item
              label="Centralized Check Printing"
              name="centralizedCheckPrinting"
              format="b"
            />
            <app-detail-item
              label="Centralized Vouchers"
              name="centralizedVouchers"
              format="b"
            />
          </app-detail-panel>
        </app-card>
      </div>
      <div class="tw-basis-1/2">
        <app-card label="Line">
          <app-detail-panel [dataSource]="planSummary.line">
            <app-detail-item label="Line" name="line" />
            <app-detail-item
              label="Policy Exists For Claims"
              name="policyExistsForClaims"
              format="b"
            />
            <app-detail-item label="Limit" name="limit" format="c" />
            <app-detail-item label="Deductible" name="deductible" format="c" />
            <app-detail-item
              label="Include Expense In Excess"
              name="includeExpenseInExcess"
              format="b"
            />
          </app-detail-panel>
        </app-card>
      </div>
    </div>
    <div class="tw-flex tw-gap-8 tw-pb-8">
      <div class="tw-basis-full">
        <app-card label="Coverage">
          <div class="tw-flex tw-gap-8 tw-pb-8">
            <div class="tw-basis-1/2">
              <app-detail-panel [dataSource]="planSummary.coverage">
                <app-detail-item label="Coverage" name="coverage" />
                <app-detail-item label="Limit" name="limit" format="c" />
                <app-detail-item
                  label="Deductible/Self insured retention"
                  name="deductible"
                  format="c"
                />
                <app-detail-item
                  label="Retro Date"
                  name="retroDate"
                  format="d"
                />
              </app-detail-panel>
            </div>
            <div class="tw-basis-1/2">
              <app-detail-panel [dataSource]="planSummary.coverage">
                <app-detail-item label="Tail Date" name="tailDate" format="d" />
                <app-detail-item label="Pay Category" name="payCategory" />
                <app-detail-item
                  label="Claims Made"
                  name="claimsMade"
                  format="b"
                />
                <app-detail-item
                  label="Allow Recoveries On Involved Claims"
                  name="allowRecoveriesOnInvolvedClaims"
                  format="b"
                />
              </app-detail-panel>
            </div>
          </div>
        </app-card>
      </div>
    </div>
    <div class="tw-flex tw-gap-8 tw-pb-8">
      <div class="tw-basis-full">
        <app-card label="Policy">
          <div class="tw-flex tw-gap-8 tw-pb-8">
            <div class="tw-basis-1/2">
              <app-detail-panel [dataSource]="planSummary.policy">
                <app-detail-item label="Policy Number" name="policyNumber" />
                <app-detail-item
                  label="Inception Date"
                  name="inceptionDate"
                  format="d"
                />
                <app-detail-item label="Carrier" name="carrier" />
              </app-detail-panel>
            </div>
            <div class="tw-basis-1/2">
              <app-detail-panel [dataSource]="planSummary.policy">
                <app-detail-item label="Description" name="description" />
                <app-detail-item label="Type" name="type" />
                <app-detail-item
                  label="Expiration Date"
                  name="expirationDate"
                  format="d"
                />
              </app-detail-panel>
            </div>
          </div>
          <app-tab-panel>
            <app-tab label="Agency">
              <ng-template #content>
                <app-policy-agency [dataSource]="planSummary.agency" />
              </ng-template>
            </app-tab>
            <app-tab
              label="Policy Contacts"
              [count]="planSummary.policyContacts?.length"
            >
              <ng-template #content>
                <app-policy-contacts
                  [dataSource]="{ contacts: planSummary.policyContacts }"
                />
              </ng-template>
            </app-tab>
            @if (planSummary.hasLayeredPolicy) {
              <app-tab
                label="Policy Layer"
                [count]="planSummary.layerGroups?.length"
              >
                <ng-template #content>
                  <app-policy-layer [dataSource]="planSummary.layerGroups" />
                </ng-template>
              </app-tab>
            }
            <app-tab
              label="Attributes"
              [count]="planSummary.attributes?.length"
            >
              <ng-template #content>
                <app-policy-attributes
                  [dataSource]="planSummary.attributes"
                  source="plan-summary"
                  layout="plan-summary"
                />
              </ng-template>
            </app-tab>
          </app-tab-panel>
        </app-card>
      </div>
    </div>`,
  styles: [],
})
export class PlanSummaryPageComponent extends BaseComponent implements OnInit {
  // Initialize with empty structure - data will be loaded from API
  planSummary: PlanSummaryResponse = {
    plan: {},
    line: {},
    coverage: {},
    policy: {},
    agency: { communicationMethods: [] },
    policyContacts: [],
    attributes: [],
    layerGroups: [],
    hasAgency: false,
    hasLayeredPolicy: false,
  };

  async ngOnInit(): Promise<void> {
    this.view.showLoading();
    await this.loadPlanSummary();
    this.view.hideLoading();
  }

  private async loadPlanSummary(): Promise<void> {
    const claim = this.view.currentClaim();

    const response = await this.api.planSummary.get(
      {
        recordId: claim.recordId,
        recordSource: claim.recordSource,
      },
      claim.contractNumber,
    );

    // Handle null response (when no data found in database)
    if (response) {
      this.planSummary = response;
      this.formatPlanSummary();
      this.normalizeAttributesForTable();
    }
  }

  private formatPlanSummary(): void {
    // Format Plan card fields
    if (this.planSummary.plan) {
      this.planSummary.plan.plan = this.formatNameWithDateRange(
        this.planSummary.plan.planNum,
        this.planSummary.plan.planName,
        this.planSummary.plan.planDateRange,
      );
      // Show $0.00 when loss fund is null
      if (
        this.planSummary.plan.lossFund === null ||
        this.planSummary.plan.lossFund === undefined
      ) {
        this.planSummary.plan.lossFund = 0;
      }
    }

    // Format Line card fields
    if (this.planSummary.line) {
      this.planSummary.line.line = this.formatNameWithDateRange(
        this.planSummary.line.lineNum,
        this.planSummary.line.lineName,
        this.planSummary.line.lineDateRange,
      );
      // Classic: "N" = "NO", else "YES"
      // Note: includeExpenseInExcess is displayed raw in Classic (no conversion)
    }

    // Format Coverage card fields
    if (this.planSummary.coverage) {
      this.planSummary.coverage.coverage = this.formatNameWithDateRange(
        this.planSummary.coverage.coverageNum,
        this.planSummary.coverage.coverageName,
        this.planSummary.coverage.coverageDateRange,
      );
      // Default money/flags when null to match Classic behavior
      if (
        this.planSummary.coverage.limit === null ||
        this.planSummary.coverage.limit === undefined
      ) {
        this.planSummary.coverage.limit = 0;
      }
      if (
        this.planSummary.coverage.deductible === null ||
        this.planSummary.coverage.deductible === undefined
      ) {
        this.planSummary.coverage.deductible = 0;
      }
      if (this.planSummary.coverage.claimsMade === undefined) {
        this.planSummary.coverage.claimsMade = false;
      }
      if (
        this.planSummary.coverage.allowRecoveriesOnInvolvedClaims === undefined
      ) {
        this.planSummary.coverage.allowRecoveriesOnInvolvedClaims = false;
      }
    }
  }

  // Ensure attribute fields for app-policy-attributes table (Name, Name description, Value, Value description, Field type, Deleted)
  private normalizeAttributesForTable(): void {
    if (!this.planSummary.attributes?.length) return;

    this.planSummary.attributes = this.planSummary.attributes.map((attr) => ({
      ...attr,
      deleted: attr.deleted ?? false,
    }));
  }

  // Format: "{num padded to 4 digits} {name} {dateRange}" (matches Classic)
  private formatNameWithDateRange(
    num: number | undefined,
    name: string | undefined,
    dateRange: string | undefined,
  ): string | undefined {
    if (!num) return undefined;
    const paddedNum = num.toString().padStart(4, '0');
    return `${paddedNum} ${name || ''} ${dateRange || ''}`.trim();
  }
}
